import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { todayInWorkspaceTz, type TaskFilter } from "@/hooks/useWorkspaceTasks";

/** Subtarefa atribuída ao membro, com o título do pai quando visível por RLS. */
export interface MyAssignedSubtask {
  id: string;
  title: string;
  description: string | null;
  status: "pending" | "completed" | "cancelled";
  due_date: string | null;
  due_at: string | null;
  completed_at: string | null;
  parent_task_id: string;
  parentTitle: string | null;
  /** Conclusão individual do membro nesta subtarefa. */
  my_completed_at: string | null;
}

interface AssigneeRow {
  completed_at: string | null;
  task: {
    id: string;
    title: string;
    description: string | null;
    status: MyAssignedSubtask["status"];
    due_date: string | null;
    due_at: string | null;
    completed_at: string | null;
    parent_task_id: string | null;
  } | null;
}

const SELECT = `
  completed_at,
  task:task_id (
    id, title, description, status, due_date, due_at, completed_at, parent_task_id
  )
`;

function addDaysISO(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

interface UseMyAssignedSubtasksArgs {
  workspaceId: string | null;
  memberId: string | null;
  filter: TaskFilter;
  enabled?: boolean;
  timezone?: string;
}

/**
 * Subtarefas atribuídas ao membro atual (independente do dono do pai), para a
 * aba "Minhas". Roda em uma consulta separada da listagem de tarefas-raiz para
 * não interferir com a paginação. O título do pai é buscado à parte porque o
 * RLS pode esconder o pai quando ele pertence a outro membro.
 */
export function useMyAssignedSubtasks({
  workspaceId,
  memberId,
  filter,
  enabled = true,
  timezone,
}: UseMyAssignedSubtasksArgs) {
  const [subtasks, setSubtasks] = useState<MyAssignedSubtask[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchSubtasks = useCallback(async () => {
    if (!enabled || !workspaceId || !memberId) {
      setSubtasks([]);
      return;
    }
    setLoading(true);
    try {
      const today = todayInWorkspaceTz(timezone);

      const { data, error } = await supabase
        .from("workspace_task_assignees")
        .select(SELECT)
        .eq("workspace_id", workspaceId)
        .eq("member_id", memberId);
      if (error) throw error;

      let rows = ((data ?? []) as unknown as AssigneeRow[])
        .map((row) => {
          const task = row.task;
          if (!task?.parent_task_id) return null;
          return {
            id: task.id,
            title: task.title,
            description: task.description,
            status: task.status,
            due_date: task.due_date,
            due_at: task.due_at,
            completed_at: task.completed_at,
            parent_task_id: task.parent_task_id,
            my_completed_at: row.completed_at,
          };
        })
        .filter((row): row is Omit<MyAssignedSubtask, "parentTitle"> => row !== null);

      switch (filter) {
        case "today":
          rows = rows.filter(
            (r) => r.due_date === today && !r.my_completed_at && r.status !== "cancelled",
          );
          break;
        case "overdue":
          rows = rows.filter(
            (r) =>
              r.due_date !== null &&
              r.due_date < today &&
              !r.my_completed_at &&
              r.status !== "cancelled",
          );
          break;
        case "upcoming":
          rows = rows.filter(
            (r) =>
              r.due_date !== null &&
              r.due_date > today &&
              !r.my_completed_at &&
              r.status !== "cancelled",
          );
          break;
        case "completed":
          rows = rows.filter(
            (r) =>
              r.my_completed_at !== null &&
              r.my_completed_at >= `${addDaysISO(today, -30)}T00:00:00.000Z`,
          );
          break;
        case "all":
        default:
          rows = rows.filter((r) => r.status !== "cancelled");
          break;
      }

      rows.sort((a, b) => {
        const aDue = a.due_date ?? "9999-12-31";
        const bDue = b.due_date ?? "9999-12-31";
        return aDue.localeCompare(bDue);
      });

      const parentIds = Array.from(new Set(rows.map((r) => r.parent_task_id)));
      const parentTitleById = new Map<string, string>();
      if (parentIds.length > 0) {
        const { data: parents } = await supabase
          .from("workspace_tasks")
          .select("id, title")
          .in("id", parentIds);
        (parents ?? []).forEach((p) => parentTitleById.set(p.id, p.title));
      }

      setSubtasks(
        rows.map((r) => ({
          ...r,
          parentTitle: parentTitleById.get(r.parent_task_id) ?? null,
        })),
      );
    } catch (err: unknown) {
      toast({
        title: "Erro ao carregar suas subtarefas",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [enabled, workspaceId, memberId, filter, timezone, toast]);

  useEffect(() => {
    void fetchSubtasks();
  }, [fetchSubtasks]);

  const toggleMyCompletion = useCallback(
    async (taskId: string, completed: boolean) => {
      if (!memberId) return;
      const { error } = await supabase
        .from("workspace_task_assignees")
        .update({ completed_at: completed ? new Date().toISOString() : null })
        .eq("task_id", taskId)
        .eq("member_id", memberId);
      if (error) throw error;
      await fetchSubtasks();
    },
    [memberId, fetchSubtasks],
  );

  return { subtasks, loading, refetch: fetchSubtasks, toggleMyCompletion };
}
